import { ToastData } from 'base-components/Toast/Toast';
import ToastContainer from 'base-components/Toast/ToastContainer';
import { TOAST_PROPERTIES } from 'base-components/Toast/toastProperties';
import useLocalStorage from 'hooks/useLocalStorage';
import React, { Dispatch, useContext, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

export type Toasts = {
    id: string;
    title: string;
    description: string;
};

export type ToastAdd = Pick<ToastData, 'type' | 'title' | 'description' | 'data' | 'progress'> & {
    autoDeleteTime?: number | null;
};

export type ToastProviderState = {
    removeToast: (id: string) => void;
    addToast: (toast: ToastAdd) => string;
    updateToast: (update: Partial<ToastData> & { id: string }) => void;
};

type ToastProviderProps = React.PropsWithChildren;

const ToastContext = React.createContext<ToastProviderState>({} as any);

const ToastProvider = ({ children }: ToastProviderProps) => {
    const [toasts, setToasts] = useLocalStorage<ToastData[]>('toasts', []) as [
        ToastData[],
        Dispatch<React.SetStateAction<ToastData[]>>,
    ];
    const [currentToasts, setCurrentToasts] = useState<ToastData[]>(toasts);

    const addToast = ({
        type,
        title,
        description,
        data = [],
        progress,
        autoDeleteTime = 5000,
    }: ToastAdd) => {
        const toastProperties = TOAST_PROPERTIES.find(
            (toast) => toast.type.toLowerCase() === type.toLowerCase()
        );

        if (!toastProperties) {
            throw new Error('Wrong toast type!');
        }

        const newToast: ToastData = {
            ...toastProperties,
            progress,
            data,
            title,
            description,
            autoDeleteTime:
                autoDeleteTime === null || type === 'warning' || type === 'danger'
                    ? undefined
                    : autoDeleteTime,
            id: uuidv4(),
        };
        setToasts([newToast, ...toasts]);

        return newToast.id;
    };

    const removeToast = (id: string) => {
        setToasts((toasts) => [...toasts.filter((t) => t.id !== id)]);
    };

    const updateToast = (update: Partial<ToastData> & { id: string }) => {
        const toast = toasts.find((e) => e.id === update.id);
        if (toast) {
            for (const k in update) {
                (toast as any)[k] = update[k as keyof ToastData];
            }

            setToasts([...toasts]);
        }
    };

    const onStorageUpdate = (e: StorageEvent) => {
        const { key, newValue } = e;
        if (key === 'toasts') {
            setCurrentToasts(JSON.parse(newValue as string));
        }
    };

    useEffect(() => {
        setCurrentToasts(JSON.parse(localStorage.getItem('toasts') as string));
    }, [toasts]);

    useEffect(() => {
        window.addEventListener('storage', onStorageUpdate);
        return () => {
            window.removeEventListener('storage', onStorageUpdate);
        };
    }, []);

    return (
        <ToastContext.Provider
            value={{
                removeToast,
                addToast,
                updateToast,
            }}
        >
            {children}
            <ToastContainer toastList={currentToasts} removeToast={removeToast} />
        </ToastContext.Provider>
    );
};

const useToastContext = () => {
    const context = useContext(ToastContext);

    return context;
};

export { ToastProvider, useToastContext };
export default ToastProvider;
